/**
 * Interest Calculator Utility
 * 
 * Calculates simple interest based on frequency (Monthly, Quarterly, Annually)
 * Matches backend calculation formulas exactly for consistency
 * 
 * Rate is treated as per-frequency-period rate:
 * - MONTHLY: rate is per month (e.g., 2% = 2% each month)
 * - QUARTERLY: rate is per quarter (e.g., 2% = 2% each quarter)
 * - ANNUALLY: rate is per year (e.g., 2% = 2% each year)
 * 
 * Formula: Interest = Principal × (Rate/100) × number_of_periods
 */

const PERIODS_PER_YEAR = {
  MONTHLY: 12,
  QUARTERLY: 4,
  ANNUALLY: 1
};

/**
 * Calculate simple interest for the full duration
 * 
 * @param {number} principal - Principal amount
 * @param {number} rate - Interest rate per period (percentage, e.g., 2 for 2%)
 * @param {number} durationMonths - Duration in months
 * @param {string} frequency - Interest frequency (MONTHLY, QUARTERLY, ANNUALLY)
 * @returns {number} Calculated total interest amount
 * 
 * Examples (₹10,000 at 2% for 12 months):
 * - MONTHLY (2%/mo):   10000 × 0.02 × 12 = ₹2,400
 * - QUARTERLY (2%/qtr): 10000 × 0.02 × 4 = ₹800
 * - ANNUALLY (2%/yr):  10000 × 0.02 × 1 = ₹200
 */
export const calculateInterest = (principal, rate, durationMonths, frequency) => {
  if (!principal || !rate || !durationMonths || !frequency) {
    return 0;
  }

  const periodsPerYear = PERIODS_PER_YEAR[frequency];
  if (!periodsPerYear) {
    console.error(`Invalid frequency: ${frequency}`);
    return 0;
  }

  const monthsPerPeriod = 12 / periodsPerYear;
  const numberOfPeriods = durationMonths / monthsPerPeriod;
  const interest = (principal * rate * numberOfPeriods) / 100;
  return Math.round(interest * 100) / 100; // Round to 2 decimal places
};

/**
 * Calculate total amount (principal + interest)
 * 
 * @param {number} principal - Principal amount
 * @param {number} rate - Interest rate (percentage)
 * @param {number} durationMonths - Duration in months
 * @param {string} frequency - Interest frequency (MONTHLY, QUARTERLY, ANNUALLY)
 * @returns {number} Total amount (principal + interest)
 */
export const calculateTotalAmount = (principal, rate, durationMonths, frequency) => {
  if (!principal) {
    return 0;
  }

  const interest = calculateInterest(principal, rate, durationMonths, frequency);
  return Math.round((principal + interest) * 100) / 100; // Round to 2 decimal places
};

/**
 * Get available frequency options
 * 
 * @returns {Array} Array of frequency options
 */
export const getFrequencyOptions = () => {
  return [
    { value: 'MONTHLY', label: 'interest.frequency_monthly' },
    { value: 'QUARTERLY', label: 'interest.frequency_quarterly' },
    { value: 'ANNUALLY', label: 'interest.frequency_annually' }
  ];
};
